import { useState } from 'react';
import { CheckCircle2, Eye, EyeOff, KeyRound, MailWarning } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useSearch } from 'wouter';
import { validatePassword } from '@shared/validation';

import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { apiRequest, ApiError } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

// Mirrors the server's `resetPasswordRequestSchema` (server/lib/auth-validation.ts)
// so the user gets accurate feedback before submitting rather than a generic
// server rejection.
const resetPasswordSchema = z
  .object({
    password: z.string().refine(validatePassword, {
      message:
        'Password must be at least 8 characters with uppercase, lowercase, number, and special character',
    }),
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const search = useSearch();
  const token = new URLSearchParams(search).get('token') ?? '';
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const handleSubmit = async (data: ResetPasswordForm) => {
    setIsLoading(true);
    try {
      await apiRequest('POST', '/api/auth/reset-password', {
        token,
        password: data.password,
      });
      form.reset();
      setIsDone(true);
    } catch (error) {
      // Surface the server's specific reason (expired token, complexity) rather
      // than a generic failure — an expired link needs a different user action
      // than a weak password.
      let description = 'Unable to reset your password. Please try again.';
      if (error instanceof ApiError) {
        try {
          const parsed = JSON.parse(error.body) as { error?: string };
          if (parsed?.error) description = parsed.error;
        } catch {
          // non-JSON body — keep the generic message
        }
      }
      toast({ title: 'Reset Failed', description, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  // A link with no token can't be completed — send them back to request a fresh one.
  if (!token) {
    return (
      <PublicShell routeKey="account">
        <section className="page-shell-narrow py-12">
          <Card className="rounded-[2rem] border-border/70 shadow-sm">
            <CardContent className="space-y-6 p-8 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <MailWarning className="h-8 w-8" />
              </div>
              <div className="space-y-3">
                <p className="editorial-kicker">Account security</p>
                <h1 className="text-4xl font-semibold">Reset link missing</h1>
                <p className="section-copy">
                  Open the link from your password reset email, or request a fresh one from the sign
                  in page.
                </p>
              </div>
              <div className="flex flex-col justify-center gap-3 sm:flex-row">
                <Link href="/login">
                  <Button className="rounded-full px-5">Go to sign in</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>
      </PublicShell>
    );
  }

  if (isDone) {
    return (
      <PublicShell routeKey="account">
        <section className="page-shell-narrow py-12">
          <Card className="rounded-[2rem] border-border/70 shadow-sm">
            <CardContent className="space-y-6 p-8 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <div className="space-y-3">
                <p className="editorial-kicker">Account security</p>
                <h1 className="text-4xl font-semibold">Password updated</h1>
                <p className="section-copy">
                  Your password has been reset. You can now sign in with your new password.
                </p>
              </div>
              <div className="flex flex-col justify-center gap-3 sm:flex-row">
                <Link href="/login">
                  <Button className="rounded-full px-5" data-testid="link-signin-after-reset">
                    Sign in
                  </Button>
                </Link>
                <Link href="/">
                  <Button variant="outline" className="rounded-full px-5">
                    Browse Kalovate
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>
      </PublicShell>
    );
  }

  return (
    <PublicShell routeKey="account">
      <section className="page-shell-narrow py-12">
        <Card className="rounded-[2rem] border-border/70 shadow-sm">
          <CardContent className="space-y-6 p-8">
            <div className="space-y-3 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <KeyRound className="h-8 w-8" />
              </div>
              <p className="editorial-kicker">Account security</p>
              <h1 className="text-4xl font-semibold">Choose a new password</h1>
              <p className="section-copy">
                Enter a new password for your Kalovate account. This reset link can only be used
                once.
              </p>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            className="pr-10"
                            data-testid="input-new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(v => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm new password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            className="pr-10"
                            data-testid="input-confirm-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(v => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full py-5 text-base"
                  disabled={isLoading}
                  data-testid="button-reset-password"
                >
                  {isLoading ? 'Updating...' : 'Update password'}
                </Button>
              </form>
            </Form>

            <p className="text-center text-sm text-muted-foreground">
              <Link href="/login" className="hover:text-primary transition-colors">
                Back to sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </section>
    </PublicShell>
  );
}
